import * as React from 'react';

import moment from 'moment';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {PromoStateOverlay} from 'shells/promo-state-overlay/promo-state-overlay';
import {ItemCountTable} from 'shells/table/item-count-table';

import {MonthPicker} from '../../reports/month-picker';

import {ChartPageBanner} from './chart-page-banner';
import {
    ChartCard,
    ChartHeader,
    DomainsBarChart,
    TrafficDonutChart,
    TrafficOverTimeWrapper,
} from './charts';
import {useGetCompetitorTrafficReport, useGetCompetitorIqWebsites} from './graphql/hooks';
import {isAfterFifteenthOfMonth, sortBucketsAndAddStarAndFill} from './helpers';

import styles from './competitors.module.css';

export const CHARTS_DIV_ID = 'competitors-charts-container';

export function CompetitorsCharts() {
    // This will be updated to default to 1 once  we figure out getting last month's data on the 1st
    const [selectedMonth, setSelectedMonth] = React.useState<{
        id: number;
        label: string;
    }>({
        id: isAfterFifteenthOfMonth ? 1 : 2,
        label: isAfterFifteenthOfMonth
            ? 'Last month'
            : moment().subtract(2, 'months').format('MMMM YYYY'),
    });
    const lastMonthName = moment().subtract(1, 'months').format('MMMM YYYY');
    const monthName = selectedMonth.id === 1 ? lastMonthName : selectedMonth.label;

    const {website} = useGetCompetitorIqWebsites();

    const {
        trafficReportData,
        isError: trafficReportError,
        isLoading: trafficReportLoading,
    } = useGetCompetitorTrafficReport(selectedMonth.id);

    const domains = trafficReportData
        ? sortBucketsAndAddStarAndFill(trafficReportData, website)
        : [];

    const getComingSoonText = (reportName: string) => {
        if (!isAfterFifteenthOfMonth && selectedMonth.id === 1) {
            return `${reportName} data for ${lastMonthName} will be available later this month`;
        }
    };

    // Only include domains with traffic in sources bar chart
    const domainsWithTraffic = domains.filter((domain) => domain.count > 0);

    // If all domains have 0 count, hide the traffic by source chart and the donut chart
    const allDomainsHaveLowTraffic = !domainsWithTraffic.length;

    const hasFullCompetitorIq = NutClientConfig.hasFullCompetitorIq;

    return (
        <div id={CHARTS_DIV_ID}>
            <StackLegacy spacing={16}>
                <ChartHeader
                    headerTitle='Market share'
                    headerBody='Gain in-depth insights into your competitive position by analyzing
                    market share'
                    headerRight={
                        <div className='flex align-center'>
                            <MonthPicker
                                numMonths={6}
                                selectedMonth={selectedMonth.id}
                                setSelectedMonth={(month: {id: number; label: string}) => {
                                    setSelectedMonth(month);
                                }}
                            />
                        </div>
                    }
                    downloadReportTitle={`CompetitorSpy market share report for ${monthName}`}
                />
                <ChartPageBanner />
                <ChartCard
                    isLoading={trafficReportLoading}
                    isError={trafficReportError}
                    comingSoonText={getComingSoonText('Market share')}
                    content={
                        <div className={styles['donut-wrapper']}>
                            {allDomainsHaveLowTraffic ? undefined : (
                                <TrafficDonutChart domains={domains} width={250} />
                            )}
                            <div className={styles['table-container']}>
                                {hasFullCompetitorIq ? undefined : (
                                    <PromoStateOverlay
                                        promoStateProps={{
                                            isHorizontal: true,
                                            imageUrl:
                                                '/include/images/onboarding/analytics-info.svg',
                                            title: 'Upgrade to see competitor information',
                                            subtitle:
                                                'Upgrade to Marketing Pro to view see your competitor’s traffic and market share %',
                                            primaryCta: {
                                                text: 'Learn more',
                                                url: 'https://support.nutshell.com/en/articles/8561063-competitorspy-stay-one-step-ahead-of-the-game-with-competitor-website-insights',
                                                isExternalLink: true,
                                            },
                                            secondaryCta: {
                                                text: 'View plans',
                                                url: '/setup/billing/sales',
                                            },
                                            maxHeight: '70%',
                                        }}
                                        offset={{top: 68, left: 0}}
                                    />
                                )}
                                <ItemCountTable
                                    title='Website'
                                    items={domains}
                                    countColumnTitle='Visitors'
                                    zeroCountText='<1,000'
                                    includePercentage={true}
                                    percentageColumnTitle='Market share (%)'
                                    zeroPercentageText='N/A'
                                    hasHoverState={false}
                                    disableSorting={true}
                                    getInvalidPopoverContent={(domain: string) => (
                                        <div className='pad-16' style={{width: 200}}>
                                            <div className='mb-8'>
                                                Unfortunately, there’s not enough data to show
                                                information for <strong>{domain}</strong> this
                                                month.
                                            </div>
                                            <Link
                                                variant='primary'
                                                newTab={true}
                                                size='small'
                                                href='https://support.nutshell.com/en/articles/8561063-competitorspy-stay-one-step-ahead-of-the-game-with-competitor-website-insights'
                                            >
                                                Why does this happen?
                                            </Link>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    }
                />
                {allDomainsHaveLowTraffic ? undefined : (
                    <StackLegacy spacing={16}>
                        <ChartHeader
                            headerTitle='Traffic strategies'
                            headerBody='Identify competitor tactics for achieving web traffic success'
                            downloadReportTitle={`CompetitorSpy traffic by source report for ${monthName}`}
                        />
                        <ChartCard
                            isLoading={trafficReportLoading}
                            isError={trafficReportError}
                            comingSoonText={getComingSoonText('Traffic strategy')}
                            content={
                                // @ts-expect-error
                                <DomainsBarChart website={website} domains={domainsWithTraffic} />
                            }
                            promoStateOverlay={
                                hasFullCompetitorIq ? undefined : (
                                    <PromoStateOverlay
                                        promoStateProps={{
                                            isHorizontal: true,
                                            imageUrl:
                                                '/include/images/onboarding/analytics-info.svg',
                                            title: 'Identify competitor tactics for achieving web traffic success',
                                            subtitle:
                                                'Upgrade to Marketing Pro to view a competitor breakdown of web traffic',
                                            primaryCta: {
                                                text: 'Learn more',
                                                url: 'https://support.nutshell.com/en/articles/8561063-competitorspy-stay-one-step-ahead-of-the-game-with-competitor-website-insights',
                                                isExternalLink: true,
                                            },
                                            secondaryCta: {
                                                text: 'View plans',
                                                url: '/setup/billing/sales',
                                            },
                                        }}
                                    />
                                )
                            }
                        />
                    </StackLegacy>
                )}

                {/* Wait until trafficReportData is fetched before loading trafficOverTimeData to avoid
                race condition */}
                {domains.length > 0 && website ? (
                    <TrafficOverTimeWrapper website={website} />
                ) : undefined}
            </StackLegacy>
        </div>
    );
}
